import React, { useState } from 'react';
import { Button } from '../shared/Button';
import { Card } from '../shared/Card';
interface PersonalizeMoreProps {
  userData: any;
  updateUserData: (field: string, value: string) => void;
  onNext: () => void;
  onBack: () => void;
  currentStep: number;
}
export const PersonalizeMore = ({
  userData,
  updateUserData,
  onNext,
  onBack,
  currentStep
}: PersonalizeMoreProps) => {
  const [inputValue, setInputValue] = useState('');
  const [selectedGoals, setSelectedGoals] = useState<string[]>(userData.goal ? [userData.goal] : []);
  const [selectedSchedule, setSelectedSchedule] = useState(userData.schedule || '');
  const toggleGoalSelection = (goal: string) => {
    if (selectedGoals.includes(goal)) {
      setSelectedGoals(selectedGoals.filter(g => g !== goal));
    } else {
      setSelectedGoals([...selectedGoals, goal]);
    }
  };
  const handleSubmit = () => {
    if (currentStep === 0) {
      // Join multiple goals with comma if there are multiple selections
      updateUserData('goal', selectedGoals.join(','));
    } else if (currentStep === 1) {
      updateUserData('schedule', selectedSchedule);
    } else if (currentStep === 2) {
      updateUserData('healthContext', inputValue);
    }
    setInputValue('');
    onNext();
  };
  const goalOptions = [{
    value: 'consistency',
    label: 'Consistency',
    description: 'Build a regular routine'
  }, {
    value: 'energy',
    label: 'Energy',
    description: 'Feel more energized'
  }, {
    value: 'weight',
    label: 'Weight Management',
    description: 'Maintain or lose weight'
  }, {
    value: 'flexibility',
    label: 'Flexibility',
    description: 'Improve range of motion'
  }, {
    value: 'longevity',
    label: 'Longevity',
    description: 'Support long-term health'
  }];
  const scheduleOptions = [{
    value: 'morning',
    label: 'Morning',
    description: 'Start your day active'
  }, {
    value: 'midday',
    label: 'Midday',
    description: 'Break up your day'
  }, {
    value: 'evening',
    label: 'Evening',
    description: 'Wind down with movement'
  }, {
    value: 'flexible',
    label: 'Flexible',
    description: 'Different times each day'
  }];
  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <>
            <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-756">
              What are your fitness goals?
            </h1>
            <p className="text-gray-600 mb-6" data-id="element-757">
              This helps us focus your workouts on what matters most to you.
              Select all that apply.
            </p>
            <div className="space-y-3 mb-8" data-id="element-758">
              {goalOptions.map(option => <Card key={option.value} onClick={() => toggleGoalSelection(option.value)} active={selectedGoals.includes(option.value)} className="p-4" data-id="element-759">
                  <div className="flex items-center" data-id="element-760">
                    <div className="flex-1" data-id="element-761">
                      <h3 className="font-medium" data-id="element-762">{option.label}</h3>
                      <p className="text-sm text-gray-500" data-id="element-763">
                        {option.description}
                      </p>
                    </div>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${selectedGoals.includes(option.value) ? 'bg-blue-500' : 'border border-gray-300'}`} data-id="element-764">
                      {selectedGoals.includes(option.value) && <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-765">
                          <path d="M20 6 9 17l-5-5" data-id="element-766"></path>
                        </svg>}
                    </div>
                  </div>
                </Card>)}
            </div>
          </>;
      case 1:
        return <>
            <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-767">
              When do you prefer to work out?
            </h1>
            <p className="text-gray-600 mb-6" data-id="element-768">
              We'll suggest workouts that fit your schedule.
            </p>
            <div className="space-y-3 mb-8" data-id="element-769">
              {scheduleOptions.map(option => <Card key={option.value} onClick={() => setSelectedSchedule(option.value)} active={selectedSchedule === option.value} className="p-4" data-id="element-770">
                  <h3 className="font-medium" data-id="element-771">{option.label}</h3>
                  <p className="text-sm text-gray-500" data-id="element-772">{option.description}</p>
                </Card>)}
            </div>
          </>;
      case 2:
        return <>
            <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-773">
              Any health considerations we should know?
            </h1>
            <p className="text-gray-600 mb-6" data-id="element-774">
              This helps us suggest safer, more effective workouts.
            </p>
            <div className="mb-4" data-id="element-775">
              <div className="bg-blue-50 rounded-lg p-3 mb-4" data-id="element-776">
                <p className="text-sm text-blue-700" data-id="element-777">
                  This info helps us tailor your workouts with joint-friendly
                  options and personalized modifications.
                </p>
              </div>
            </div>
            <textarea value={inputValue} onChange={e => setInputValue(e.target.value)} placeholder="Type any health considerations here..." className="w-full p-4 border border-gray-300 rounded-xl h-32 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-778" />
            <div className="mt-4 flex items-center" data-id="element-779">
              <button className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-500" data-id="element-780">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-781">
                  <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" data-id="element-782"></path>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2" data-id="element-783"></path>
                  <line x1="12" x2="12" y1="19" y2="22" data-id="element-784"></line>
                </svg>
              </button>
              <span className="ml-2 text-sm text-gray-500" data-id="element-785">
                Tap to use voice input
              </span>
            </div>
            <p className="mt-4 text-sm text-gray-700" data-id="element-786">
              🔒 Your info stays private, you control what's shared.
            </p>
          </>;
      default:
        return null;
    }
  };
  return <div className="flex flex-col h-full p-6" data-id="element-787">
      <div className="mb-6" data-id="element-788">
        <button onClick={onBack} className="text-gray-500 mb-4" data-id="element-789">
          ← Back
        </button>
        {renderStep()}
      </div>
      <div className="mt-auto" data-id="element-790">
        <Button onClick={handleSubmit} variant="primary" fullWidth disabled={currentStep === 0 && selectedGoals.length === 0 || currentStep === 1 && !selectedSchedule} data-id="element-791">
          Continue
        </Button>
        <button className="w-full text-center mt-4 text-gray-500" onClick={onNext} data-id="element-792">
          Skip this step
        </button>
      </div>
    </div>;
};